import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import type { DescriptionLine } from "../descriptionTokens.js";

const branchIsExcluded = (
	branch: QuickFilterNodeDTO,
	excluded_branches: Set<string>
) =>
	excluded_branches.has(
		JSON.stringify({
			level: branch.level,
			label: branch.label,
		})
	);

const childHasExcludedBranch = (
	branch: QuickFilterNodeDTO,
	excluded_branches: Set<string>
): boolean => {
	if (
		excluded_branches.size &&
		branch.children.some((child) => branchIsExcluded(child, excluded_branches))
	) {
		return true;
	}
	return branch.children.some((child) =>
		childHasExcludedBranch(child, excluded_branches)
	);
};

export const getChildrenTokensForBranch = (
	branch: QuickFilterNodeDTO,
	selectedValue
): DescriptionLine[] => {
	const childrenTokens: DescriptionLine[] = [];

	const children = findChildrenByLabel(
		[branch],
		selectedValue.label,
		selectedValue
	);

	if (!children) return [];

	const excludedBranches = new Set<string>(
		selectedValue.excluded_branches.map(JSON.stringify)
	);

	for (const child of children) {
		if (branchIsExcluded(child, excludedBranches)) {
			continue;
		}
		const tokens: DescriptionLine = [{ text: child.visibleLabel }];
		if (childHasExcludedBranch(child, excludedBranches)) {
			tokens.push({ text: " (partial)", bold: true });
		}
		childrenTokens.push(tokens);
	}

	return childrenTokens;
};

export const getChildrenLabelsForBranch = (
	branch: QuickFilterNodeDTO,
	selectedValue
): string[] =>
	getChildrenTokensForBranch(branch, selectedValue).map((tokens) =>
		tokens.map((token) => token.text).join("")
	);

export const findChildrenByLabel = (objectTree, label, selectedValue) => {
	const selectionDepth = selectedValue.selection_depth ?? 1;

	// Recursive function to find the target node
	const findTargetNode = (
		nodes,
		targetLabel: string | null,
		level: number | null
	) => {
		for (const node of nodes) {
			if (
				(node.label === targetLabel || targetLabel === null) &&
				(node.level === level || level === null)
			) {
				return node;
			}

			if (node.children) {
				const foundNode = findTargetNode(node.children, targetLabel, level);
				if (foundNode) return foundNode;
			}
		}
		return null;
	};

	// Recursive function to collect children at a certain depth
	const collectChildrenAtDepth = (node, depth) => {
		if (node?.isSelected === false) {
			return [];
		}

		if (depth === 1) {
			return node.children || [];
		}

		return node.children.flatMap((child) =>
			collectChildrenAtDepth(child, depth - 1)
		);
	};

	const targetNode = findTargetNode(objectTree, label, selectedValue.level);
	return targetNode ? collectChildrenAtDepth(targetNode, selectionDepth) : null;
};
